import type { AgUiEvent } from "../agui";
import type { Citation, Text } from "../types";

export const invoiceCitations: Citation[] = [
  {
    id: "doc-ap-policy-4.2",
    title: "AP policy §4.2 — Payment terms",
    source: "finance-policies",
    uri: "kb://policies/travel-allowance",
    collection: "policies-kb",
    version: "v1",
    passage: 0,
    snippet: "Invoices above 50,000 SAR require approval by a finance approver before payment is scheduled.",
    score: 0.91,
    classification: "internal",
  },
  {
    id: "erp-inv-1042",
    title: "ERP invoice INV-1042",
    source: "erp",
    uri: "erp://invoices/INV-1042",
    snippet: "Supplier: Gulf Office Supplies · Amount: 72,500.00 SAR · Due: 2026-10-05",
    score: 0.87,
    classification: "confidential",
  },
];

const answer1: Text = {
  en: "Invoice INV-1042 from Gulf Office Supplies for 72,500.00 SAR is verified and due on 5 October. Because it exceeds 50,000 SAR, scheduling the payment needs a finance approver.",
  ar: "تم التحقق من الفاتورة INV-1042 من شركة الخليج للمستلزمات المكتبية بمبلغ 72,500.00 ريال، وتستحق في 5 أكتوبر. ولأنها تتجاوز 50,000 ريال، تحتاج جدولة الدفع إلى اعتماد من مسؤول مالي.",
};
const answer2: Text = {
  en: "The payment for INV-1042 is scheduled for 5 October (reference PAY-88231).",
  ar: "تمت جدولة دفع الفاتورة INV-1042 بتاريخ 5 أكتوبر (المرجع PAY-88231).",
};

/** Splits text into stream-like deltas (by words). */
export function deltas(text: string, size = 3): string[] {
  const words = text.split(/(\s+)/);
  const out: string[] = [];
  for (let i = 0; i < words.length; i += size * 2) out.push(words.slice(i, i + size * 2).join(""));
  return out;
}

function message(id: string, text: string): AgUiEvent[] {
  return [
    { type: "TEXT_MESSAGE_START", messageId: id, role: "assistant" },
    ...deltas(text).map((delta) => ({ type: "TEXT_MESSAGE_CONTENT", messageId: id, delta })),
    { type: "TEXT_MESSAGE_END", messageId: id },
  ];
}

/** Phase 1 of the invoice run: up to the approval request. */
export function invoiceRunPhase1(runId: string, lang: "ar" | "en", taskId: string): AgUiEvent[] {
  return [
    { type: "RUN_STARTED", runId, threadId: runId },
    { type: "STEP_STARTED", stepName: "retrieve" },
    { type: "CUSTOM", name: "retrieval_results", value: { results: invoiceCitations } },
    { type: "STEP_FINISHED", stepName: "retrieve" },
    { type: "STEP_STARTED", stepName: "think" },
    { type: "TOOL_CALL_START", tool: "erp", action: "get_invoice", node: "lookup" },
    { type: "TOOL_CALL_END", tool: "erp", ok: true, node: "lookup" },
    ...message(`${runId}-m1`, answer1[lang]),
    { type: "STEP_FINISHED", stepName: "think" },
    { type: "STEP_STARTED", stepName: "approve" },
    { type: "CUSTOM", name: "approval_requested", task_id: taskId, node: "approve", kind: "approval", approver_roles: ["finance-approver"], reasons: ["amount above threshold"] },
  ];
}

/** Phase 2: after the approval decision reaches the workflow. */
export function invoiceRunPhase2(runId: string, lang: "ar" | "en", taskId: string, approved: boolean, approver = "test-user"): AgUiEvent[] {
  if (!approved) {
    return [
      { type: "CUSTOM", name: "approval_decided", task_id: taskId, decision: "reject", approver },
      { type: "STEP_FINISHED", stepName: "approve" },
      ...message(`${runId}-m2`, lang === "ar" ? "تم رفض طلب الدفع، ولم تتم جدولة أي دفعة." : "The payment request was rejected; nothing was scheduled."),
      { type: "RUN_FINISHED", runId },
    ];
  }
  return [
    { type: "CUSTOM", name: "approval_decided", task_id: taskId, decision: "approve", approver },
    { type: "STEP_FINISHED", stepName: "approve" },
    { type: "STEP_STARTED", stepName: "pay" },
    { type: "TOOL_CALL_START", tool: "erp", action: "schedule_payment", node: "pay" },
    { type: "TOOL_CALL_END", tool: "erp", ok: true, node: "pay" },
    { type: "STEP_FINISHED", stepName: "pay" },
    { type: "CUSTOM", name: "safety_check", direction: "input", decision: "allow" },
    ...message(`${runId}-m2`, answer2[lang]),
    { type: "CUSTOM", name: "safety_check", direction: "output", decision: "allow" },
    { type: "RUN_FINISHED", runId },
  ];
}

export interface RecordedRun {
  id: string;
  agent: string;
  title: Text;
  recorded_at: string;
  events: AgUiEvent[];
}

/** Recorded event logs for the builder's trace replay. */
export const recordedRuns: RecordedRun[] = [
  {
    id: "run-rec-001",
    agent: "invoice-assistant@1.2.0",
    title: { en: "Invoice question with approved payment", ar: "استفسار فاتورة مع دفع معتمد" },
    recorded_at: "2026-09-30T11:20:00Z",
    events: [...invoiceRunPhase1("run-rec-001", "en", "task-rec-1"), ...invoiceRunPhase2("run-rec-001", "en", "task-rec-1", true)],
  },
  {
    id: "run-rec-002",
    agent: "invoice-assistant@1.2.0",
    title: { en: "Tool failure and run error", ar: "فشل أداة وخطأ في التشغيل" },
    recorded_at: "2026-09-29T08:05:00Z",
    events: [
      { type: "RUN_STARTED", runId: "run-rec-002" },
      { type: "STEP_STARTED", stepName: "think" },
      { type: "TOOL_CALL_START", tool: "erp", action: "get_invoice", node: "lookup" },
      { type: "TOOL_CALL_END", tool: "erp", ok: false, node: "lookup" },
      { type: "STEP_FINISHED", stepName: "think" },
      { type: "RUN_ERROR", message: "erp.get_invoice: upstream timeout after 3 retries", code: "TOOL_FAILED" },
    ],
  },
];

// ---------------------------------------------------------------- chat with documents (ADR-0067)

export const PILOT_FEE: Text = {
  en: "According to the attached agreement, the pilot fee is 12,500 SAR.",
  ar: "وفقًا للاتفاقية المرفقة، رسوم المرحلة التجريبية 12,500 ريال.",
};
export const PILOT_FEE_WORDS: Text = {
  en: "In words: twelve thousand five hundred Saudi riyals.",
  ar: "كتابةً: اثنا عشر ألفًا وخمسمائة ريال سعودي.",
};
export const NO_CONTEXT: Text = {
  en: "I don't have an earlier question or document in this conversation. Attach the document and ask again.",
  ar: "لا يوجد سؤال سابق أو مستند في هذه المحادثة. أرفق المستند واسأل مرة أخرى.",
};

/** The answer of a document chat turn: from the document, from the earlier turn, or neither. */
export function documentAnswer(question: string, documents: number, turns: number, lang: "ar" | "en"): string {
  if (turns > 0 && /word|كتاب|بالحروف/i.test(question)) return PILOT_FEE_WORDS[lang];
  return documents > 0 ? PILOT_FEE[lang] : NO_CONTEXT[lang];
}

/** A chat turn that reads attached documents and uses the earlier conversation (ADR-0067). */
export function documentRun(runId: string, opts: { lang: "ar" | "en"; question: string; documents: { id: string; name: string }[]; turns: number }): AgUiEvent[] {
  return [
    { type: "RUN_STARTED", runId, threadId: runId },
    ...opts.documents.map((d) => ({ type: "CUSTOM", name: "document_read", document_id: d.id, document_name: d.name, allowed: true, pages: 2, read_pages: 2, truncated: false, classification: "internal", reasons: [] })),
    ...(opts.turns > 0 ? [{ type: "CUSTOM", name: "conversation_context", turns: opts.turns }] : []),
    { type: "STEP_STARTED", segment: 1 },
    ...message(`${runId}-m1`, documentAnswer(opts.question, opts.documents.length, opts.turns, opts.lang)),
    { type: "STEP_FINISHED", segment: 1, trail: ["think", "done"], labels: { think: { en: "Answer the question", ar: "الإجابة عن السؤال" } } },
    { type: "RUN_FINISHED", runId, threadId: runId },
  ];
}


// ---------------------------------------------------------------- padel courts with a spoken summary (ADR-0070)

export const PADEL_SPOKEN: Text = {
  en: "7 clubs in Dubai have free courts tomorrow evening; the cheapest is 365 Clubhouse at 160 dirhams at 6 pm.",
  ar: "لدى 7 نوادٍ في دبي ملاعب متاحة مساء الغد؛ الأرخص 365 كلوب هاوس بسعر 160 درهماً الساعة 6 مساءً.",
};
const PADEL_TABLE: Text = {
  en: "**Free padel courts in Dubai: tomorrow, 18:00–22:00**\n\n| Club | Free start times | Price |\n|---|---|---|\n| 365 Clubhouse | 18:00, 19:30 | AED 160 / 60 min |\n| Padel Pro Al Quoz | 20:00 | AED 200 / 60 min |",
  ar: "**ملاعب البادل المتاحة في دبي: غداً، 18:00–22:00**\n\n| النادي | أوقات البدء المتاحة | السعر |\n|---|---|---|\n| 365 كلوب هاوس | 18:00، 19:30 | 160 درهم / 60 دقيقة |\n| بادل برو القوز | 20:00 | 200 درهم / 60 دقيقة |",
};

/** A tool-built answer with a table and a short spoken version (the padel courts finder). */
export function padelRun(runId: string, lang: "ar" | "en"): AgUiEvent[] {
  const id = `${runId}-answer`;
  return [
    { type: "RUN_STARTED", runId, threadId: runId },
    { type: "STEP_STARTED", segment: 1 },
    { type: "TEXT_MESSAGE_START", messageId: id, role: "assistant", format: "markdown", files: [], spoken: PADEL_SPOKEN[lang] },
    { type: "TEXT_MESSAGE_CONTENT", messageId: id, delta: PADEL_TABLE[lang] },
    { type: "TEXT_MESSAGE_END", messageId: id },
    { type: "STEP_FINISHED", segment: 1, trail: ["understand", "pick", "clubs", "report"], labels: { report: { en: "Write the answer", ar: "كتابة الإجابة" } } },
    { type: "RUN_FINISHED", runId, threadId: runId, spoken: PADEL_SPOKEN[lang] },
  ];
}

/** ADR-0100 demo: "printer broken, open a ticket" — the answer claims a ticket no system opened. */
export function isClaimQuestion(q: string): boolean {
  return /open a ticket|افتح تذكرة/i.test(q);
}

export function claimRun(runId: string, lang: "ar" | "en"): AgUiEvent[] {
  const text = lang === "ar" ? "تم فتح تذكرة برقم 12345 وسيتواصل معك الفريق." : "I have opened a ticket for you (#12345); the team will contact you.";
  const mid = `${runId}-m1`;
  return [
    { type: "RUN_STARTED", runId, threadId: runId },
    { type: "TEXT_MESSAGE_START", messageId: mid, role: "assistant" },
    { type: "TEXT_MESSAGE_CONTENT", messageId: mid, delta: text },
    { type: "TEXT_MESSAGE_END", messageId: mid },
    {
      type: "CUSTOM",
      name: "claims_unbacked",
      claims: ["case"],
      text: { en: "Note: no case or ticket was actually opened.", ar: "ملاحظة: لم تُفتح أي حالة أو تذكرة فعلًا." },
    },
    { type: "RUN_FINISHED", runId },
  ];
}

export function isPadelQuestion(q: string): boolean {
  return /padel|بادل|البادل/i.test(q);
}
